import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Fuse from 'fuse.js';
import { Search, X, Loader2, Wrench, Plane, Users, Handshake, Trophy, FileText, LayoutGrid } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_META = {
  'build-logs': { icon: Wrench, en: 'Build Logs', tr: 'Yapım Günlükleri', color: '#00F0FF' },
  'aircraft': { icon: Plane, en: 'Aircraft Specifications', tr: 'İHA Teknik Özellikleri', color: '#38BDF8' },
  'team': { icon: Users, en: 'Team & Roles', tr: 'Ekip', color: '#D8B4FE' },
  'sponsors': { icon: Handshake, en: 'Sponsors & Institutional Patronage', tr: 'Sponsorlar', color: '#FDE68A' },
  'achievements': { icon: Trophy, en: 'Achievements', tr: 'Başarılar', color: '#F97316' },
  'documents': { icon: FileText, en: 'Documents', tr: 'Dökümanlar', color: '#94A3B8' },
  'pages': { icon: LayoutGrid, en: 'Pages', tr: 'Sayfalar', color: '#64748B' },
};

const CATEGORY_ORDER = ['build-logs', 'aircraft', 'team', 'sponsors', 'achievements', 'documents', 'pages'];

const highlightRange = (text, start, end) => (
  <>
    {text.slice(0, start)}
    <mark style={{ background: 'rgba(0, 240, 255, 0.25)', color: '#00F0FF', padding: '0 2px' }}>
      {text.slice(start, end)}
    </mark>
    {text.slice(end)}
  </>
);

const findMatchRange = (text, query, fieldMatches) => {
  if (!text) return null;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx !== -1) return [idx, idx + query.length];
  if (fieldMatches?.indices?.length) {
    const longest = fieldMatches.indices.reduce((a, b) => (b[1] - b[0] > a[1] - a[0] ? b : a));
    if (longest[1] - longest[0] >= 1) return [longest[0], longest[1] + 1];
  }
  return null;
};

const highlight = (text, query, fieldMatches) => {
  const range = findMatchRange(text, query, fieldMatches);
  if (!range) return text;
  return highlightRange(text, range[0], range[1]);
};

const makeSnippet = (body, query, fieldMatches, maxLen = 140) => {
  if (!body) return { text: '', range: null };
  const range = findMatchRange(body, query, fieldMatches);
  if (!range) return { text: body.slice(0, maxLen) + (body.length > maxLen ? '…' : ''), range: null };
  const start = Math.max(0, range[0] - 50);
  const end = Math.min(body.length, range[1] + 90);
  const prefix = start > 0 ? '…' : '';
  const text = prefix + body.slice(start, end) + (end < body.length ? '…' : '');
  return { text, range: [range[0] - start + prefix.length, range[1] - start + prefix.length] };
};

const SearchModal = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const [index, setIndex] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const { language } = useLanguage();

  useEffect(() => {
    if (!isOpen) return;
    setQuery('');
    setTimeout(() => inputRef.current?.focus(), 50);
    if (!index) {
      setLoading(true);
      setFetchError(false);
      fetch(`${API_BASE_URL}/api/search/index`)
        .then((r) => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.json();
        })
        .then((d) => setIndex(d.items || []))
        .catch(() => { setIndex(null); setFetchError(true); })
        .finally(() => setLoading(false));
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  const fuse = useMemo(() => {
    if (!index) return null;
    return new Fuse(index, {
      keys: [
        { name: 'title', weight: 1.0 },
        { name: 'title_tr', weight: 1.0 },
        { name: 'section', weight: 0.7 },
        { name: 'body', weight: 0.4 },
        { name: 'body_tr', weight: 0.4 },
      ],
      threshold: 0.35,
      ignoreLocation: true,
      minMatchCharLength: 2,
      includeScore: true,
      includeMatches: true,
    });
  }, [index]);

  const suggestions = useMemo(() => {
    if (!index) return [];
    const seen = new Set();
    const picks = [];
    for (const item of index) {
      if (['build-logs', 'aircraft', 'sponsors'].includes(item.category) && item.title) {
        const word = item.title.split(/[:\-–]/)[0].trim().split(' ').slice(0, 2).join(' ');
        if (word.length >= 3 && !seen.has(word.toLowerCase())) {
          seen.add(word.toLowerCase());
          picks.push(word);
        }
      }
      if (picks.length >= 5) break;
    }
    return picks;
  }, [index]);

  const grouped = useMemo(() => {
    if (!fuse || query.trim().length < 2) return null;
    const results = fuse.search(query.trim(), { limit: 30 });
    const groups = {};
    results.forEach(({ item, matches }) => {
      if (!groups[item.category]) groups[item.category] = [];
      if (groups[item.category].length < 6) {
        const matchMap = {};
        (matches || []).forEach((m) => { if (!matchMap[m.key]) matchMap[m.key] = m; });
        groups[item.category].push({ item, matchMap });
      }
    });
    return groups;
  }, [fuse, query]);

  const handleSelect = useCallback((item) => {
    onClose();
    navigate(item.url);
  }, [navigate, onClose]);

  if (!isOpen) return null;

  const q = query.trim();
  const hasResults = grouped && Object.keys(grouped).length > 0;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-start justify-center px-4 pt-[10vh]"
      style={{ background: 'rgba(11, 15, 23, 0.75)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={language === 'tr' ? 'Site içi arama' : 'Site search'}
      data-testid="search-modal-overlay"
    >
      <div
        className="w-full max-w-2xl rounded-xl overflow-hidden animate-in fade-in slide-in-from-top-4 duration-200"
        style={{
          background: 'rgba(15, 23, 42, 0.92)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(0, 240, 255, 0.25)',
          boxShadow: '0 0 60px rgba(0, 240, 255, 0.15), 0 24px 60px rgba(0, 0, 0, 0.6)',
        }}
        onClick={(e) => e.stopPropagation()}
        data-testid="search-modal"
      >
        {/* Search Input */}
        <div className="flex items-center gap-3 px-5 py-4" style={{ borderBottom: '1px solid rgba(0, 240, 255, 0.15)' }}>
          {loading ? (
            <Loader2 size={22} className="animate-spin flex-shrink-0" style={{ color: '#00F0FF' }} data-testid="search-loading-spinner" />
          ) : (
            <Search size={22} className="flex-shrink-0" style={{ color: '#00F0FF' }} />
          )}
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={language === 'tr'
              ? 'Bütün sitede ara... (günlükler, algoritmalar, İHA özellikleri, sponsorlar)'
              : 'Search engineering logs, AI algorithms, aircraft specs, sponsors...'}
            className="flex-1 bg-transparent outline-none text-lg"
            style={{ color: '#F8FAFC', caretColor: '#00F0FF' }}
            aria-label={language === 'tr' ? 'Arama kutusu' : 'Search input'}
            data-testid="search-input"
          />
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg transition-colors duration-200 hover:bg-[rgba(255,255,255,0.1)]"
            style={{ color: '#94A3B8' }}
            aria-label="Close search"
            data-testid="search-close-btn"
          >
            <X size={20} />
          </button>
        </div>

        {/* Results */}
        <div className="max-h-[55vh] overflow-y-auto" data-testid="search-results-container">
          {q.length < 2 && !fetchError && (
            <div className="px-5 py-10 text-center">
              <p className="body-small" style={{ color: '#64748B' }}>
                {language === 'tr'
                  ? 'Aramaya başlamak için en az 2 karakter yazın'
                  : 'Type at least 2 characters to start searching'}
              </p>
              {suggestions.length > 0 && (
                <div className="flex flex-wrap justify-center gap-2 mt-4">
                  {suggestions.map((s) => (
                    <button
                      key={s}
                      onClick={() => setQuery(s)}
                      className="px-3 py-1 text-xs rounded-full transition-all duration-200 hover:scale-105"
                      style={{ color: '#00F0FF', border: '1px solid rgba(0, 240, 255, 0.3)', background: 'rgba(0, 240, 255, 0.05)' }}
                      data-testid={`search-suggestion-${s.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {fetchError && (
            <div className="px-5 py-10 text-center" data-testid="search-error-state">
              <p className="body-medium mb-2" style={{ color: '#F8FAFC' }}>
                {language === 'tr' ? 'Arama indeksi yüklenemedi.' : 'Failed to load search index.'}
              </p>
              <button
                onClick={() => { setIndex(null); setFetchError(false); setLoading(true);
                  fetch(`${API_BASE_URL}/api/search/index`)
                    .then((r) => { if (!r.ok) throw new Error(); return r.json(); })
                    .then((d) => setIndex(d.items || []))
                    .catch(() => setFetchError(true))
                    .finally(() => setLoading(false)); }}
                className="px-4 py-2 text-sm rounded-lg"
                style={{ color: '#00F0FF', border: '1px solid rgba(0, 240, 255, 0.4)' }}
                data-testid="search-retry-btn"
              >
                {language === 'tr' ? 'Tekrar Dene' : 'Retry'}
              </button>
            </div>
          )}

          {q.length >= 2 && !hasResults && !loading && !fetchError && (
            <div className="px-5 py-10 text-center" data-testid="search-empty-state">
              <p className="body-medium mb-2" style={{ color: '#F8FAFC' }}>
                {language === 'tr'
                  ? `'${q}' için teknik kayıt bulunamadı.`
                  : `No technical records found for '${q}'.`}
              </p>
              <p className="body-small" style={{ color: '#64748B' }}>
                {language === 'tr'
                  ? "Şunları deneyin: 'EDF', 'YOLOv11', 'Concentric', 'Ray-Casting' veya 'Dener'."
                  : "Try searching 'EDF', 'YOLOv11', 'Concentric', 'Ray-Casting', or 'Dener'."}
              </p>
            </div>
          )}

          {hasResults && CATEGORY_ORDER.filter((c) => grouped[c]).map((cat) => {
            const meta = CATEGORY_META[cat] || CATEGORY_META.pages;
            const Icon = meta.icon;
            return (
              <div key={cat} className="py-2" data-testid={`search-category-${cat}`}>
                <div className="flex items-center gap-2 px-5 py-2">
                  <Icon size={14} style={{ color: meta.color }} />
                  <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: meta.color }}>
                    {language === 'tr' ? meta.tr : meta.en}
                  </span>
                </div>
                {grouped[cat].map(({ item, matchMap }) => {
                  const useTr = language === 'tr' && item.title_tr;
                  const title = useTr ? item.title_tr : item.title;
                  const titleMatch = matchMap[useTr ? 'title_tr' : 'title'] || matchMap.title_tr || matchMap.title;
                  const useBodyTr = language === 'tr' && item.body_tr;
                  const bodyMatch = matchMap[useBodyTr ? 'body_tr' : 'body'];
                  let body = useBodyTr ? item.body_tr : item.body;
                  let bodyFieldMatch = bodyMatch;
                  if (!findMatchRange(body, q, bodyMatch)) {
                    const altBody = useBodyTr ? item.body : item.body_tr;
                    const altMatch = matchMap[useBodyTr ? 'body' : 'body_tr'];
                    if (findMatchRange(altBody, q, altMatch)) { body = altBody; bodyFieldMatch = altMatch; }
                  }
                  const snippet = makeSnippet(body, q, bodyFieldMatch);
                  return (
                    <button
                      key={item.id}
                      onClick={() => handleSelect(item)}
                      className="w-full text-left px-5 py-3 transition-colors duration-150 hover:bg-[rgba(0,240,255,0.08)] focus:bg-[rgba(0,240,255,0.08)] outline-none"
                      style={{ borderLeft: '2px solid transparent' }}
                      onMouseOver={(e) => e.currentTarget.style.borderLeftColor = meta.color}
                      onMouseOut={(e) => e.currentTarget.style.borderLeftColor = 'transparent'}
                      data-testid={`search-result-${item.id}`}
                    >
                      <div className="body-medium font-medium mb-0.5" style={{ color: '#F8FAFC' }}>
                        {highlight(title, q, titleMatch)}
                      </div>
                      {item.section && (
                        <div className="text-xs mb-1" style={{ color: '#94A3B8' }}>{item.section}</div>
                      )}
                      <div className="text-sm leading-snug" style={{ color: '#64748B' }}>
                        {snippet.range ? highlightRange(snippet.text, snippet.range[0], snippet.range[1]) : snippet.text}
                      </div>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-2.5" style={{ borderTop: '1px solid rgba(0, 240, 255, 0.1)', background: 'rgba(11, 15, 23, 0.5)' }}>
          <span className="text-xs" style={{ color: '#475569' }}>
            {language === 'tr' ? 'Kapatmak için ESC' : 'ESC to close'}
          </span>
          <span className="text-xs" style={{ color: '#475569' }}>
            Kartal Air {language === 'tr' ? 'Site İçi Arama' : 'Site Search'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default SearchModal;
